import { useGameStore } from '../store/gameStore'
import { Heart, Crosshair, Clock, Users, MapPin } from 'lucide-react'

interface GameHUDProps {
  onPause: () => void
}

export function GameHUD({ onPause }: GameHUDProps) {
  const { health, ammo, maxAmmo, kills, timeRemaining, zoneRadius } = useGameStore()

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      {/* Top Bar */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
        {/* Health */}
        <div className="panel px-4 py-2 flex items-center gap-3">
          <Heart size={24} className={health > 50 ? 'text-oded-success' : 'text-oded-danger'} />
          <div>
            <div className="text-xs text-white/60">Health</div>
            <div className="flex items-center gap-2">
              <div className="w-32 h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    health > 50 ? 'bg-oded-success' : health > 25 ? 'bg-oded-warning' : 'bg-oded-danger'
                  }`}
                  style={{ width: `${health}%` }}
                />
              </div>
              <span className="text-sm font-bold">{Math.round(health)}</span>
            </div>
          </div>
        </div>

        {/* Time & Zone */}
        <div className="panel px-4 py-2 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-oded-accent" />
            <span className="text-lg font-bold font-mono">{formatTime(timeRemaining)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-oded-warning" />
            <span className="text-sm">Zone: {Math.round(zoneRadius)}m</span>
          </div>
        </div>

        {/* Stats */}
        <div className="panel px-4 py-2 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Crosshair size={18} className="text-oded-accent" />
            <span className="text-sm">{ammo}/{maxAmmo}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users size={18} className="text-oded-danger" />
            <span className="text-sm">{kills} Kills</span>
          </div>
        </div>
      </div>

      {/* Minimap */}
      <div className="absolute bottom-4 right-4 panel p-2">
        <div className="w-32 h-32 bg-oded-dark/80 rounded-lg relative overflow-hidden">
          <div className="absolute inset-0 border border-white/20 rounded-lg" />
          <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-oded-accent rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 w-20 h-20 border border-oded-success/50 rounded-full -translate-x-1/2 -translate-y-1/2" />
        </div>
      </div>

      {/* Controls hint */}
      <div className="absolute bottom-4 left-4 text-white/40 text-xs">
        <div>WASD — Move | Mouse — Look | Click — Shoot | ESC — Pause</div>
      </div>

      {/* Pause button */}
      <button
        onClick={onPause}
        className="absolute top-4 right-1/2 translate-x-1/2 panel px-4 py-2 
                   pointer-events-auto hover:bg-white/10 transition-colors"
      >
        <span className="text-sm text-white/70">ESC to Pause</span>
      </button>
    </div>
  )
}